import { useMemo, useState } from 'react';
import { Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Area, ComposedChart } from 'recharts';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { calculatePortfolioPnL } from '../../lib/calculations';
import { formatCurrencyCompact, formatNumber } from '../../lib/formatters';
import { GRID_PROPS, X_AXIS_PROPS, Y_AXIS_PROPS, TOOLTIP_STYLE, CHART_COLORS } from '../../styles/chart-theme';
import { InfoButton } from '../common/Tooltip';
import { Slider } from '../common/Slider';
import { Calendar } from 'lucide-react';
import { useIsMobile } from '../../hooks/useIsMobile';

export function TimeSeriesProjection() {
  const isMobile = useIsMobile();
  const packages = useSimulatorStore(s => s.packages);
  const assumptions = useSimulatorStore(s => s.assumptions);
  const [monthlyGrowthRate, setMonthlyGrowthRate] = useState(0.05);

  const data = useMemo(() => {
    const months: { month: number; label: string; s1Leads: number; monthlyPnL: number; cumulativePnL: number; s2Units: number }[] = [];
    let cumulative = 0;

    for (let m = 1; m <= 12; m++) {
      const growthMultiplier = Math.pow(1 + monthlyGrowthRate, m - 1);
      const adjustedAssumptions = { ...assumptions, s1Leads: Math.round(assumptions.s1Leads * growthMultiplier) };
      const result = calculatePortfolioPnL(packages, adjustedAssumptions);
      const monthlyPnL = result.portfolioPnLSplit;
      cumulative += monthlyPnL;

      months.push({
        month: m,
        label: `M${m}`,
        s1Leads: adjustedAssumptions.s1Leads,
        monthlyPnL: Math.round(monthlyPnL),
        cumulativePnL: Math.round(cumulative),
        s2Units: Math.round(result.totalS2UnitsSplit),
      });
    }

    return months;
  }, [packages, assumptions, monthlyGrowthRate]);

  const breakevenMonth = data.findIndex(d => d.cumulativePnL >= 0);
  const finalCumulative = data[data.length - 1]?.cumulativePnL ?? 0;
  const finalMonthlyPnL = data[data.length - 1]?.monthlyPnL ?? 0;

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-1">
        <Calendar size={16} className="text-chart-blue" />
        <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">12-Month P&L Projection</h3>
        <InfoButton helpKey="timeSeriesProjection" size={13} />
      </div>
      <p className="text-sm text-muted mb-4">
        Cumulative P&L over 12 months with compound S1 lead growth. Based on current portfolio configuration and split demand.
      </p>

      <div className="flex items-center gap-4 mb-4 px-1">
        <span className="text-xs text-muted shrink-0">S1 Lead Growth:</span>
        <Slider
          value={monthlyGrowthRate}
          min={0}
          max={0.15}
          step={0.01}
          onChange={setMonthlyGrowthRate}
        />
        <span className="text-xs font-mono text-chart-blue font-semibold w-12 shrink-0">+{(monthlyGrowthRate * 100).toFixed(0)}%/mo</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="px-3 py-2 rounded-lg" style={{ backgroundColor: 'var(--color-surface)' }}>
          <div className="text-xs text-muted uppercase">Year-End Cumulative</div>
          <div className={`text-lg font-bold font-mono ${finalCumulative >= 0 ? 'text-metric-positive' : 'text-metric-negative'}`}>
            {formatCurrencyCompact(finalCumulative)}
          </div>
        </div>
        <div className="px-3 py-2 rounded-lg" style={{ backgroundColor: 'var(--color-surface)' }}>
          <div className="text-xs text-muted uppercase">Month 12 P&L</div>
          <div className={`text-lg font-bold font-mono ${finalMonthlyPnL >= 0 ? 'text-metric-positive' : 'text-metric-negative'}`}>
            {formatCurrencyCompact(finalMonthlyPnL)}
          </div>
        </div>
        <div className="px-3 py-2 rounded-lg" style={{ backgroundColor: 'var(--color-surface)' }}>
          <div className="text-xs text-muted uppercase">Breakeven Month</div>
          <div className="text-lg font-bold font-mono text-primary">
            {breakevenMonth >= 0 ? `M${breakevenMonth + 1}` : 'Not in Y1'}
          </div>
        </div>
        <div className="px-3 py-2 rounded-lg" style={{ backgroundColor: 'var(--color-surface)' }}>
          <div className="text-xs text-muted uppercase">M12 S1 Leads</div>
          <div className="text-lg font-bold font-mono text-primary">
            {formatNumber(data[11]?.s1Leads ?? 0)}
          </div>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={isMobile ? 220 : 320}>
        <ComposedChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
          <CartesianGrid {...GRID_PROPS} />
          <XAxis dataKey="label" {...X_AXIS_PROPS} />
          <YAxis {...Y_AXIS_PROPS} tickFormatter={v => formatCurrencyCompact(v)} />
          <Tooltip
            {...TOOLTIP_STYLE}
            formatter={(value: number, name: string) => [formatCurrencyCompact(value), name === 'cumulativePnL' ? 'Cumulative P&L' : 'Monthly P&L']}
            labelFormatter={l => `Month ${l.replace('M', '')}`}
          />
          <ReferenceLine y={0} stroke="var(--color-reference-line)" strokeWidth={2} />
          {breakevenMonth >= 0 && (
            <ReferenceLine
              x={`M${breakevenMonth + 1}`}
              stroke={CHART_COLORS.emerald}
              strokeDasharray="6 3"
              strokeWidth={2}
              label={{ value: 'Breakeven', fill: CHART_COLORS.emerald, fontSize: 11, fontWeight: 700, position: 'top' }}
            />
          )}
          <Area type="monotone" dataKey="cumulativePnL" fill={CHART_COLORS.blue} fillOpacity={0.1} stroke={CHART_COLORS.blue} strokeWidth={2.5} dot={{ r: 4, fill: CHART_COLORS.blue, stroke: 'var(--color-dot-stroke)', strokeWidth: 2 }} />
          <Line type="monotone" dataKey="monthlyPnL" stroke={CHART_COLORS.teal} strokeWidth={1.5} strokeDasharray="5 3" dot={{ r: 3, fill: CHART_COLORS.teal }} />
        </ComposedChart>
      </ResponsiveContainer>

      <div className="flex items-center gap-4 mt-3 text-xs text-muted">
        <span className="flex items-center gap-1"><span className="w-3 h-0.5 rounded" style={{ backgroundColor: CHART_COLORS.blue }} /> Cumulative P&L</span>
        <span className="flex items-center gap-1"><span className="w-3 h-0.5 rounded" style={{ backgroundColor: CHART_COLORS.teal, borderStyle: 'dashed' }} /> Monthly P&L</span>
      </div>
    </div>
  );
}
